import { MapPin, TrendingUp, TrendingDown, Minus, Volume2, Construction, TreePine, Users, BarChart3, TrendingUpIcon, ChevronDown, Train, Zap, Wallet, Home, Share2, Printer, Copy, Check, Coffee, Shield, DollarSign } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useState, useEffect } from "react";
import { trackReportGenerated } from "@/services/analytics";
import { toast } from "@/hooks/use-toast";
import { QuickStatCard, getSentimentFromImpact, formatStatValue } from "@/components/QuickStatCard";

interface CategoryData {
  summary: string;
  impact: string;
}

interface PropertyReportProps {
  report?: {
    address: string;
    tldr?: string;
    accessibility?: CategoryData;
    noise_traffic?: CategoryData;
    local_development?: CategoryData;
    environment?: CategoryData;
    demographics?: CategoryData;
    market_dynamics?: CategoryData;
    gentrification_index?: CategoryData;
    investor_thought?: string;
    owner_occupier_thought?: string;
    mapImageUrl?: string; // NEW: HERE Map Image URL
  };
  quickFacts?: {
    address: string;
    raw_data: any;
    scores: any;
  };
  isLoadingAI?: boolean; // True when AI enhancement is in progress
  onReset: () => void;
  isSampleReport?: boolean; // True for pre-generated sample reports
}

const getImpactBadge = (impact: string) => {
  const lowerImpact = impact.toLowerCase();
  
  if (lowerImpact.includes("positive")) {
    return <Badge className="bg-green-500/10 text-green-700 border-green-500/20">🟢 Positive</Badge>;
  }
  if (lowerImpact.includes("negative")) {
    return <Badge className="bg-red-500/10 text-red-700 border-red-500/20">🔴 Negative</Badge>;
  }
  return <Badge className="bg-yellow-500/10 text-yellow-700 border-yellow-500/20">🟡 Neutral</Badge>;
};

const getNoiseLevel = (impact: string) => {
  const lowerImpact = impact.toLowerCase();
  
  if (lowerImpact.includes("quiet")) {
    return <div className="flex items-center gap-2"><span className="text-sm font-medium">Quiet</span><div className="flex-1 h-2 bg-green-500/20 rounded-full"><div className="h-full w-1/3 bg-green-500 rounded-full"></div></div></div>;
  }
  if (lowerImpact.includes("noisy")) {
    return <div className="flex items-center gap-2"><span className="text-sm font-medium">Noisy</span><div className="flex-1 h-2 bg-red-500/20 rounded-full"><div className="h-full w-full bg-red-500 rounded-full"></div></div></div>;
  }
  return <div className="flex items-center gap-2"><span className="text-sm font-medium">Moderate</span><div className="flex-1 h-2 bg-yellow-500/20 rounded-full"><div className="h-full w-2/3 bg-yellow-500 rounded-full"></div></div></div>;
};

const getMarketTrend = (impact: string) => {
  const lowerImpact = impact.toLowerCase();
  
  if (lowerImpact.includes("rising")) {
    return <div className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-green-500" /><span className="font-semibold">Rising</span></div>;
  }
  if (lowerImpact.includes("cooling")) {
    return <div className="flex items-center gap-2"><TrendingDown className="h-5 w-5 text-red-500" /><span className="font-semibold">Cooling</span></div>;
  }
  return <div className="flex items-center gap-2"><Minus className="h-5 w-5 text-yellow-500" /><span className="font-semibold">Stable</span></div>;
};

const getDemographicEmoji = (impact: string) => {
  const lowerImpact = impact.toLowerCase();
  
  if (lowerImpact.includes("positive")) return "😀";
  if (lowerImpact.includes("negative")) return "😞";
  return "😐";
};

// Stat Badge Component for inline number highlighting
const StatBadge = ({ value, label, color = "blue" }: { value: string; label?: string; color?: "blue" | "green" | "red" | "orange" | "purple" }) => {
  const colorClasses = {
    blue: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20",
    green: "bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20",
    red: "bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/20",
    orange: "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-500/20",
    purple: "bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20",
  };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-sm font-semibold border ${colorClasses[color]}`}>
      {value}
      {label && <span className="text-xs opacity-70">{label}</span>}
    </span>
  );
};

// Parse summary into structured format (intro + bullets + closing)
const parseSummary = (summary: string) => {
  if (!summary) return { intro: '', bullets: [], closing: '' };
  
  const lines = summary.split('\n').filter(line => line.trim());
  
  // Detect if already has bullets (starts with • or -)
  const hasBullets = lines.some(line => line.trim().startsWith('•') || line.trim().startsWith('-'));
  
  if (hasBullets) {
    // Already formatted - split into intro, bullets, closing
    const bullets = lines.filter(line => line.trim().startsWith('•') || line.trim().startsWith('-'));
    const text = lines.filter(line => !line.trim().startsWith('•') && !line.trim().startsWith('-'));
    
    return {
      intro: text[0] || '',
      bullets: bullets.map(b => b.replace(/^[•\-]\s*/, '').trim()),
      closing: text.length > 1 ? text[text.length - 1] : '',
    };
  }
  
  // Not formatted - return as single intro for backwards compatibility
  return {
    intro: summary,
    bullets: [],
    closing: '',
  };
};

// Highlight numbers and stats with colored badges
const highlightStats = (text: string): React.ReactNode => {
  if (!text) return null;
  
  // Patterns to match and their colors (order matters - most specific first)
  const patterns = [
    { regex: /\$[\d,]+\.?\d*[KMB]?/g, color: 'green' as const },  // Money: $1.2M, $875k
    { regex: /\d+\.?\d*%/g, color: 'blue' as const },  // Percentages: 8.3%, 45%
    { regex: /\d+\.?\d*\s*(?:km|m)\b/gi, color: 'orange' as const },  // Distance: 1.5km, 600m (must be before time)
    { regex: /\d+\.?\d*\s*(?:days?|mins?|minutes?|hours?|hrs?|seconds?|secs?)\b/gi, color: 'orange' as const },  // Time: 5 mins, 2.5 hours
    { regex: /\d+\.?\d*\s*(?:cafes?|schools?|parks?|routes?|roads?|services?)\b/gi, color: 'purple' as const },  // Counts: 5 schools, 101 cafes
  ];
  
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const matches: Array<{ index: number; match: string; color: typeof patterns[0]['color'] }> = [];
  
  // Find all matches
  patterns.forEach(({ regex, color }) => {
    const regexCopy = new RegExp(regex.source, regex.flags);
    let match;
    while ((match = regexCopy.exec(text)) !== null) {
      matches.push({ index: match.index, match: match[0], color });
    }
  });
  
  // Sort matches by index
  matches.sort((a, b) => a.index - b.index);
  
  // Remove overlapping matches (keep first occurrence)
  const uniqueMatches = matches.filter((match, i) => {
    if (i === 0) return true;
    return match.index >= matches[i - 1].index + matches[i - 1].match.length;
  });
  
  // Build React nodes
  uniqueMatches.forEach((match, i) => {
    // Add text before match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }
    // Add badge
    parts.push(<StatBadge key={`stat-${i}`} value={match.match} color={match.color} />);
    lastIndex = match.index + match.match.length;
  });
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  
  return <>{parts}</>;
};

// Extract transit and routing data from raw_data
const getTransitData = (quickFacts: any) => {
  if (!quickFacts?.raw_data) return null;
  
  const transportFreq = quickFacts.raw_data.transportFrequency;
  const routing = quickFacts.raw_data.routingData;
  
  if (!transportFreq) return null;
  
  return {
    station: transportFreq.nearestStation,
    busRoutes: transportFreq.busRoutes,
    walkingTime: routing?.walkingTimeToStation,
    cbdCommute: routing?.multiModalCommute,
  };
};

// Extract headline stats for collapsed view
const getAccessibilityHeadline = (summary: string, quickFacts?: any) => {
  if (!summary) return {};
  
  // Try to get data from raw_data first, fallback to parsing summary
  let train = null;
  let cbd = null;
  let cafes = null;
  let schools = null;
  
  // Train station distance
  if (quickFacts?.raw_data?.poi?.nearest?.train_station) {
    const distanceM = quickFacts.raw_data.poi.nearest.train_station;
    train = distanceM >= 1000 ? `${(distanceM / 1000).toFixed(1)}km` : `${distanceM}m`;
  } else {
    const trainMatch = summary.match(/(\d+\.?\d*)\s*(km|m)\s+away/i);
    if (trainMatch) train = trainMatch[1] + trainMatch[2];
  }
  
  // CBD commute time
  if (quickFacts?.raw_data?.routingData?.multiModalCommute?.totalDurationMinutes) {
    cbd = `${quickFacts.raw_data.routingData.multiModalCommute.totalDurationMinutes}min`;
  } else {
    const cbdMatch = summary.match(/(\d+)\s*mins?\s*(?:direct\s*)?(?:to\s*)?CBD/i);
    if (cbdMatch) cbd = `${cbdMatch[1]}min`;
  }
  
  // Cafes (conversational)
  if (quickFacts?.raw_data?.poi?.categories?.hospitality !== undefined) {
    const count = quickFacts.raw_data.poi.categories.hospitality;
    cafes = formatConversationalNumber(count, 'cafe');
  }
  
  // Schools (conversational)
  if (quickFacts?.raw_data?.poi?.categories?.education !== undefined) {
    const count = quickFacts.raw_data.poi.categories.education;
    schools = formatConversationalNumber(count, 'school');
  }
  
  return {
    train,
    cbd,
    cafes,
    schools,
  };
};

const getNoiseHeadline = (summary: string) => {
  if (!summary) return {};
  const roadsMatch = summary.match(/(\d+)\s*major\s*roads?/i);
  const impact = summary.toLowerCase().includes('quiet') ? 'Quiet' : 
                 summary.toLowerCase().includes('noisy') || summary.toLowerCase().includes('very noisy') ? 'Noisy' : 'Moderate';
  
  return {
    roads: roadsMatch ? `${roadsMatch[1]} roads` : null,
    level: impact,
  };
};

const getMarketHeadline = (summary: string) => {
  if (!summary) return {};
  const priceMatch = summary.match(/\$[\d,]+\.?\d*[KMB]/);
  const growthMatch = summary.match(/(\d+\.?\d*)%\s*(?:YoY|year-on-year)/i);
  const daysMatch = summary.match(/(\d+)\s*days/i);
  
  return {
    price: priceMatch ? priceMatch[0] : null,
    growth: growthMatch ? `${growthMatch[1]}%` : null,
    days: daysMatch ? `${daysMatch[1]}d` : null,
  };
};

// Conversational number formatter
const formatConversationalNumber = (count: number, singular: string, plural?: string): string => {
  const pluralForm = plural || `${singular}s`;
  
  if (count === 0) return `no ${pluralForm}`;
  if (count === 1) return `1 ${singular}`;
  if (count === 2) return `2 ${pluralForm}`;
  if (count <= 5) return `${count} ${pluralForm}`;
  if (count <= 10) return `several ${pluralForm}`;
  if (count <= 20) return `plenty of ${pluralForm}`;
  if (count <= 50) return `heaps of ${pluralForm}`;
  if (count <= 100) return `loads of ${pluralForm}`;
  return `endless ${pluralForm}`;
};

const getEnvironmentHeadline = (summary: string, quickFacts?: any) => {
  if (!summary) return {};
  
  // Use raw data if available, otherwise parse summary text (for sample reports)
  let parksCount = null;
  if (quickFacts?.raw_data?.poi?.categories?.parks !== undefined) {
    const count = quickFacts.raw_data.poi.categories.parks;
    parksCount = formatConversationalNumber(count, 'park');
  } else {
    // Fallback: parse AI summary text
    const parksMatch = summary.match(/(?:Zero|0|(\d+))\s*(?:major\s+)?parks?/i);
    if (parksMatch) {
      if (parksMatch[0].toLowerCase().includes('zero') || parksMatch[0].includes('0')) {
        parksCount = 'no parks';
      } else if (parksMatch[1]) {
        const count = parseInt(parksMatch[1], 10);
        parksCount = formatConversationalNumber(count, 'park');
      }
    }
  }
  
  // Extract nearest distance (from raw data or summary)
  let nearest = null;
  if (quickFacts?.raw_data?.poi?.nearest?.park) {
    const distanceM = quickFacts.raw_data.poi.nearest.park;
    nearest = distanceM >= 1000 ? `${(distanceM / 1000).toFixed(1)}km` : `${distanceM}m`;
  } else {
    const nearestMatch = summary.match(/(\d+\.?\d*)\s*(km|m)\s*away/i);
    if (nearestMatch) {
      nearest = nearestMatch[1] + nearestMatch[2];
    }
  }
  
  return {
    parks: parksCount,
    nearest: nearest,
  };
};

export const PropertyReport = ({ report, quickFacts, isLoadingAI = false, onReset, isSampleReport = false }: PropertyReportProps) => {
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    accessibility: false,
    noise: false,
    development: false,
    environment: false,
    demographics: false,
    market: false,
    gentrification: false,
  });

  // Use report if available, otherwise use quickFacts for address
  const displayAddress = report?.address || quickFacts?.address || 'Unknown Address';

  // Track report generation (only for real reports, not sample reports)
  useEffect(() => {
    // Skip analytics for pre-generated sample reports
    if (isSampleReport) return;
    
    // Only track when full report is available (not just quick facts)
    if (!report) return;
    
    // Parse suburb and state from address
    const parseAddress = (address: string) => {
      const parts = address.split(',').map(p => p.trim());
      let suburb: string | undefined;
      let state: string | undefined;
      
      // Find the state
      const stateMatch = address.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/);
      state = stateMatch ? stateMatch[1] : undefined;
      
      // Find suburb (usually second to last part before the state/postcode)
      if (parts.length >= 3) {
        suburb = parts[parts.length - 2]?.replace(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/, '').trim();
      } else if (parts.length === 2) {
        suburb = parts[0];
      }
      
      return { suburb, state };
    };
    
    const addressParts = parseAddress(report.address);
    trackReportGenerated(report.address, addressParts.suburb, addressParts.state);
  }, [report?.address, isSampleReport]);

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // State for copy feedback
  const [copied, setCopied] = useState(false);

  // Share handler - uses native share or clipboard fallback
  const handleShare = async () => {
    const shareData = {
      title: `Property Report: ${displayAddress}`,
      text: `Check out this property report for ${displayAddress}`,
      url: window.location.href,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        toast({
          title: "Link copied!",
          description: "Property report link copied to clipboard",
        });
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (error) {
      // User cancelled share or error occurred
      console.log('Share cancelled or failed');
    }
  };

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // Get transit data from quickFacts
  const transitData = getTransitData(quickFacts);
  const mapImageUrl = quickFacts?.raw_data?.mapImageUrl || report?.mapImageUrl;

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6 sm:space-y-8 animate-fade-in pb-12 px-4 sm:px-6">
      {/* Hero Section - Modern Redesign */}
      <div className="relative pt-4 sm:pt-8">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-green-500/5 rounded-3xl pointer-events-none"></div>
        
        <div className="relative space-y-4 sm:space-y-6">
          {/* Top Navigation Bar */}
          <div className="flex items-center justify-between gap-4 max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30 rounded-full backdrop-blur-sm">
              <span className="text-base sm:text-lg">📊</span>
              <span className="text-xs sm:text-sm font-bold text-green-700 dark:text-green-400 uppercase tracking-wider">
            Property Report
          </span>
        </div>
        
            <div className="flex items-center gap-2">
              {/* Share/Print Actions */}
              <div className="hidden sm:flex items-center gap-1.5">
                <Button
                  onClick={handleShare}
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-muted-foreground hover:text-foreground"
                >
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Share2 className="h-4 w-4" />}
                  <span className="hidden md:inline">{copied ? "Copied!" : "Share"}</span>
                </Button>
                <Button
                  onClick={handlePrint}
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-muted-foreground hover:text-foreground no-print"
                >
                  <Printer className="h-4 w-4" />
                  <span className="hidden md:inline">Print</span>
                </Button>
              </div>
              
              <Button
                onClick={onReset}
                variant="outline"
                size="sm"
                className="gap-2 font-semibold hover:bg-primary hover:text-primary-foreground transition-all shadow-sm no-print"
              >
                <MapPin className="h-4 w-4" />
                <span className="hidden sm:inline">Search Another</span>
                <span className="sm:hidden">New</span>
              </Button>
            </div>
        </div>
        
          {/* Main Address Card */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-card/80 backdrop-blur-sm border border-border rounded-2xl shadow-xl overflow-hidden">
              {/* Address Header */}
              <div className="p-6 sm:p-8 border-b border-border/50">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex-shrink-0 ring-2 ring-primary/20">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="mb-2">
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Property Address</span>
                    </div>
                    <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground break-words leading-tight">
                      {displayAddress}
                    </h1>
                    {isLoadingAI && !report && (
                      <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
                        <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse"></div>
                        <span>Analyzing property data...</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto">
        <QuickStatCard
          icon={<Train className="h-5 w-5" />}
          value={
            quickFacts?.raw_data?.routingData?.walkingTimeToStation?.durationMinutes
              ? `${quickFacts.raw_data.routingData.walkingTimeToStation.durationMinutes} min`
              : quickFacts?.raw_data?.poi?.nearest?.train_station
                ? formatStatValue(quickFacts.raw_data.poi.nearest.train_station, "distance")
                : "N/A"
          }
          label="To Station"
          sentiment={
            quickFacts?.raw_data?.poi?.nearest?.train_station && quickFacts.raw_data.poi.nearest.train_station < 800
              ? "positive"
              : quickFacts?.raw_data?.poi?.nearest?.train_station && quickFacts.raw_data.poi.nearest.train_station > 1500
                ? "negative"
                : "neutral"
          }
        />
        <QuickStatCard
          icon={<DollarSign className="h-5 w-5" />}
          value={quickFacts?.raw_data?.propertySales?.medianPrice || report?.market_dynamics?.summary?.split('$')[1]?.split(' ')[0] ? `$${quickFacts?.raw_data?.propertySales?.medianPrice || report?.market_dynamics?.summary?.split('$')[1]?.split(' ')[0] || ''}` : "TBD"}
          label="Est. Value"
          sentiment="neutral"
        />
        <QuickStatCard
          icon={<Coffee className="h-5 w-5" />}
          value={formatStatValue(quickFacts?.raw_data?.poi?.categories?.hospitality, "count")}
          label="Cafes Nearby"
          sentiment={
            quickFacts?.raw_data?.poi?.categories?.hospitality > 20 ? "positive" : 
            quickFacts?.raw_data?.poi?.categories?.hospitality < 5 ? "negative" : "neutral"
          }
        />
        <QuickStatCard
          icon={<Shield className="h-5 w-5" />}
          value={
            report?.demographics?.impact?.toLowerCase().includes("low crime") ? "Low" :
            report?.demographics?.impact?.toLowerCase().includes("high crime") ? "High" :
            quickFacts?.raw_data?.census?.seifa_score 
              ? quickFacts.raw_data.census.seifa_score >= 7 ? "Good" : "Average"
              : "N/A"
          }
          label="Safety"
          sentiment={getSentimentFromImpact(report?.demographics?.impact)}
        />
      </div>

      {/* Location Map - NEW: HERE Map Image API */}
      {mapImageUrl && (
        <Card className="relative overflow-hidden border-2 border-primary/20 shadow-xl">
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2 sm:gap-3">
              <MapPin className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              <div>
                <span className="text-base sm:text-lg font-bold">Location Map</span>
                <p className="text-xs text-muted-foreground font-normal mt-0.5">
                  Property & nearby amenities
                </p>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative rounded-xl overflow-hidden shadow-lg border border-border/50 bg-muted/20">
              <img 
                src={mapImageUrl} 
                alt="Property location map showing nearby amenities"
                className="w-full h-auto"
                loading="eager"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  // Hide the image and show fallback message if it fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent && !parent.querySelector('.map-error')) {
                    const errorDiv = document.createElement('div');
                    errorDiv.className = 'map-error p-8 text-center text-muted-foreground';
                    errorDiv.innerHTML = '<p class="text-sm">Map temporarily unavailable</p><p class="text-xs mt-2">Location data is still included in the report</p>';
                    parent.appendChild(errorDiv);
                  }
                }}
              />
              <div className="absolute bottom-2 right-2 px-2 py-1 bg-background/90 backdrop-blur-sm rounded text-xs text-muted-foreground border border-border/50">
                Powered by HERE Maps
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* TLDR Section - BRUTAL & DRAMATIC */}
      {/* Show skeleton while AI is loading */}
      {isLoadingAI && !report?.tldr && (
        <Card className="relative overflow-hidden border-2 border-gray-300/30 bg-gradient-to-br from-gray-300/5 via-gray-200/5 to-gray-300/5 shadow-2xl animate-pulse">
          <CardHeader className="relative pb-3 sm:pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 sm:gap-3">
                <div className="h-8 w-8 bg-gray-300 rounded-full flex-shrink-0"></div>
                <div className="space-y-2">
                  <div className="h-6 w-48 bg-gray-300 rounded"></div>
                  <div className="h-3 w-32 bg-gray-200 rounded"></div>
                </div>
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="relative">
            <div className="bg-background/60 backdrop-blur-sm rounded-lg p-4 sm:p-6 border border-gray-300/20 space-y-3">
              <div className="h-5 bg-gray-300 rounded w-full"></div>
              <div className="h-5 bg-gray-300 rounded w-5/6"></div>
              <div className="h-5 bg-gray-300 rounded w-4/5"></div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {report?.tldr && (
        <Card className="relative overflow-hidden border-2 border-red-500/30 bg-gradient-to-br from-red-500/5 via-orange-500/5 to-red-500/5 shadow-2xl">
          {/* Animated background effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 via-orange-500/10 to-red-500/10 animate-pulse"></div>
          
          <CardHeader className="relative pb-3 sm:pb-4">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 sm:gap-3">
                <div className="relative flex-shrink-0">
                  <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-red-500 animate-pulse" />
                  <div className="absolute inset-0 h-6 w-6 sm:h-8 sm:w-8 text-red-500 animate-ping opacity-75">
                    <Zap className="h-6 w-6 sm:h-8 sm:w-8" />
                  </div>
                </div>
                <div>
                  <span className="text-lg sm:text-xl md:text-2xl font-black bg-gradient-to-r from-red-600 via-orange-600 to-red-600 bg-clip-text text-transparent uppercase tracking-wide">
                    The Brutal Truth
                  </span>
                  <p className="text-xs text-muted-foreground font-normal mt-0.5 sm:mt-1">
                    No sugar coating. You've been warned.
                  </p>
                </div>
              </CardTitle>
            </div>
          </CardHeader>
          
          <CardContent className="relative">
            <div className="bg-background/60 backdrop-blur-sm rounded-lg p-4 sm:p-6 border border-red-500/20">
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl leading-relaxed text-foreground font-semibold">
                {report.tldr}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section Divider - Data-Driven Insights */}
      <div className="flex items-center gap-2 sm:gap-4 py-4 sm:py-6">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary/50 to-primary/50"></div>
        <div className="flex flex-col items-center gap-0.5 sm:gap-1 px-2">
          <span className="text-xs sm:text-sm font-bold text-primary uppercase tracking-wider text-center">
            📊 Data Insights
          </span>
          <span className="text-xs text-muted-foreground hidden sm:block">We asked ChatGPT nicely for the tea ☕</span>
        </div>
        <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent"></div>
      </div>

      <div className="space-y-4 sm:space-y-5">
        {/* Accessibility - Show from quick facts OR full report */}
        {(report?.accessibility || quickFacts?.scores?.accessibility) && (() => {
          const summary = report?.accessibility?.summary || quickFacts?.scores?.accessibility?.summary || '';
          const impact = report?.accessibility?.impact || quickFacts?.scores?.accessibility?.impact || '';
          const parsed = parseSummary(summary);
          const headlines = getAccessibilityHeadline(summary, quickFacts);
          
          return (
          <Collapsible open={openSections.accessibility} onOpenChange={() => toggleSection("accessibility")}>
            <Card className="group shadow-md hover:shadow-xl transition-all duration-300 border-2 border-blue-500/20 hover:border-blue-500/40 bg-gradient-to-r from-blue-500/5 to-transparent">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between hover:bg-blue-500/5 transition-colors rounded-t-lg p-4 sm:p-6">
                    <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-blue-600/10 group-hover:from-blue-500/30 group-hover:to-blue-600/20 transition-colors shadow-sm flex-shrink-0">
                      <Train className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                    </div>
                        <div className="text-left flex-1">
                      <span className="text-base sm:text-lg md:text-xl font-bold text-foreground">Accessibility</span>
                      <p className="text-xs text-blue-600/80 font-medium">Transport & Amenities</p>
                    </div>
                  </CardTitle>
                      
                      {/* Headline stats when collapsed */}
                      {!openSections.accessibility && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {headlines.train && <StatBadge value={headlines.train} color="blue" />}
                          {headlines.cbd && <StatBadge value={headlines.cbd} label="to CBD" color="green" />}
                          {headlines.cafes && <StatBadge value={headlines.cafes} color="orange" />}
                          {headlines.schools && <StatBadge value={headlines.schools} color="purple" />}
                        </div>
                      )}
                    </div>
                    <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground transition-all duration-300 flex-shrink-0 ml-2 ${openSections.accessibility ? 'rotate-180' : ''}`} />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-2 pb-4 sm:pb-6 px-4 sm:px-6 bg-blue-500/[0.02]">
                    {/* Intro sentence */}
                    {parsed.intro && (
                      <p className="text-base font-medium text-foreground leading-relaxed">
                        {highlightStats(parsed.intro)}
                      </p>
                    )}
                    
                    {/* Bullet points */}
                    {parsed.bullets.length > 0 && (
                      <ul className="space-y-2.5">
                        {parsed.bullets.map((bullet, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                            <span className="text-blue-600 font-bold mt-0.5">•</span>
                            <span className="flex-1">{highlightStats(bullet)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {/* Closing insight */}
                    {parsed.closing && (
                      <p className="text-sm text-foreground font-medium italic mt-3">
                        {highlightStats(parsed.closing)}
                      </p>
                    )}
                    
                    <div className="mt-4">
                      {getImpactBadge(impact)}
                    </div>
                    
                    {!report && quickFacts && (
                      <p className="text-xs text-blue-600/70 italic mt-2">
                        🤖 AI-enhanced insights loading...
                      </p>
                    )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          );
        })()}

        {/* Noise & Traffic - Show from quick facts OR full report */}
        {(report?.noise_traffic || quickFacts?.scores?.noise_traffic) && (() => {
          const summary = report?.noise_traffic?.summary || quickFacts?.scores?.noise_traffic?.summary || '';
          const impact = report?.noise_traffic?.impact || quickFacts?.scores?.noise_traffic?.impact || '';
          const parsed = parseSummary(summary);
          const headlines = getNoiseHeadline(summary);
          
          return (
          <Collapsible open={openSections.noise} onOpenChange={() => toggleSection("noise")}>
            <Card className="group shadow-md hover:shadow-xl transition-all duration-300 border-2 border-orange-500/20 hover:border-orange-500/40 bg-gradient-to-r from-orange-500/5 to-transparent">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between hover:bg-orange-500/5 transition-colors rounded-t-lg p-4 sm:p-6">
                    <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-orange-500/20 to-orange-600/10 group-hover:from-orange-500/30 group-hover:to-orange-600/20 transition-colors shadow-sm flex-shrink-0">
                      <Volume2 className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600" />
                    </div>
                        <div className="text-left flex-1">
                      <span className="text-base sm:text-lg md:text-xl font-bold text-foreground">Noise & Traffic</span>
                      <p className="text-xs text-orange-600/80 font-medium">Sound Levels & Roads</p>
                    </div>
                  </CardTitle>
                      
                      {/* Headline stats when collapsed */}
                      {!openSections.noise && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {headlines.roads && <StatBadge value={headlines.roads} color="orange" />}
                          {headlines.level && <StatBadge value={headlines.level} color={headlines.level === 'Quiet' ? 'green' : headlines.level === 'Noisy' ? 'red' : 'orange'} />}
                        </div>
                      )}
                    </div>
                    <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground transition-all duration-300 flex-shrink-0 ml-2 ${openSections.noise ? 'rotate-180' : ''}`} />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-2 pb-4 sm:pb-6 px-4 sm:px-6 bg-orange-500/[0.02]">
                    {parsed.intro && (
                      <p className="text-base font-medium text-foreground leading-relaxed">
                        {highlightStats(parsed.intro)}
                      </p>
                    )}
                    
                    {parsed.bullets.length > 0 && (
                      <ul className="space-y-2.5">
                        {parsed.bullets.map((bullet, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                            <span className="text-orange-600 font-bold mt-0.5">•</span>
                            <span className="flex-1">{highlightStats(bullet)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {parsed.closing && (
                      <p className="text-sm text-foreground font-medium italic mt-3">
                        {highlightStats(parsed.closing)}
                      </p>
                    )}
                    
                    <div className="mt-4">
                      {getNoiseLevel(impact)}
                    </div>
                    
                    {!report && quickFacts && (
                      <p className="text-xs text-orange-600/70 italic mt-2">
                        🤖 AI-enhanced insights loading...
                      </p>
                    )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          );
        })()}

        {/* Local Development - Show from quick facts OR full report */}
        {(report?.local_development || quickFacts?.scores?.local_development) && (() => {
          const summary = report?.local_development?.summary || quickFacts?.scores?.local_development?.summary || '';
          const impact = report?.local_development?.impact || quickFacts?.scores?.local_development?.impact || '';
          const parsed = parseSummary(summary);
          
          return (
          <Collapsible open={openSections.development} onOpenChange={() => toggleSection("development")}>
            <Card className="group shadow-md hover:shadow-xl transition-all duration-300 border-2 border-yellow-500/20 hover:border-yellow-500/40 bg-gradient-to-r from-yellow-500/5 to-transparent">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between hover:bg-yellow-500/5 transition-colors rounded-t-lg p-4 sm:p-6">
                    <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 group-hover:from-yellow-500/30 group-hover:to-yellow-600/20 transition-colors shadow-sm flex-shrink-0">
                      <Construction className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-700" />
                    </div>
                        <div className="text-left flex-1">
                      <span className="text-base sm:text-lg md:text-xl font-bold text-foreground">Local Development</span>
                      <p className="text-xs text-yellow-700/80 font-medium">Growth & Changes</p>
                    </div>
                  </CardTitle>
                      
                      {!openSections.development && parsed.intro && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{parsed.intro.substring(0, 80)}...</p>
                      )}
                    </div>
                    <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground transition-all duration-300 flex-shrink-0 ml-2 ${openSections.development ? 'rotate-180' : ''}`} />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-2 pb-4 sm:pb-6 px-4 sm:px-6 bg-yellow-500/[0.02]">
                    {parsed.intro && (
                      <p className="text-base font-medium text-foreground leading-relaxed">
                        {highlightStats(parsed.intro)}
                      </p>
                    )}
                    
                    {parsed.bullets.length > 0 && (
                      <ul className="space-y-2.5">
                        {parsed.bullets.map((bullet, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                            <span className="text-yellow-700 font-bold mt-0.5">•</span>
                            <span className="flex-1">{highlightStats(bullet)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {parsed.closing && (
                      <p className="text-sm text-foreground font-medium italic mt-3">
                        {highlightStats(parsed.closing)}
                      </p>
                    )}
                    
                    <div className="mt-4">
                      {getImpactBadge(impact)}
                    </div>
                    
                    {!report && quickFacts && (
                      <p className="text-xs text-yellow-600/70 italic mt-2">
                        🤖 AI-enhanced insights loading...
                      </p>
                    )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          );
        })()}

        {/* Environment - Show from quick facts OR full report */}
        {(report?.environment || quickFacts?.scores?.environment) && (() => {
          const summary = report?.environment?.summary || quickFacts?.scores?.environment?.summary || '';
          const impact = report?.environment?.impact || quickFacts?.scores?.environment?.impact || '';
          const parsed = parseSummary(summary);
          const headlines = getEnvironmentHeadline(summary, quickFacts);
          
          return (
          <Collapsible open={openSections.environment} onOpenChange={() => toggleSection("environment")}>
            <Card className="group shadow-md hover:shadow-xl transition-all duration-300 border-2 border-green-500/20 hover:border-green-500/40 bg-gradient-to-r from-green-500/5 to-transparent">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between hover:bg-green-500/5 transition-colors rounded-t-lg p-4 sm:p-6">
                    <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-green-500/20 to-green-600/10 group-hover:from-green-500/30 group-hover:to-green-600/20 transition-colors shadow-sm flex-shrink-0">
                      <TreePine className="h-5 w-5 sm:h-6 sm:w-6 text-green-700" />
                    </div>
                        <div className="text-left flex-1">
                      <span className="text-base sm:text-lg md:text-xl font-bold text-foreground">Environment</span>
                      <p className="text-xs text-green-700/80 font-medium">Parks & Green Space</p>
                    </div>
                  </CardTitle>
                      
                      {/* Headline stats when collapsed */}
                      {!openSections.environment && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {headlines.parks && (
                            <StatBadge 
                              value={headlines.parks} 
                              color={headlines.parks === 'no parks' ? 'red' : 'green'} 
                            />
                          )}
                          {headlines.nearest && !headlines.parks?.includes('no parks') && <StatBadge value={headlines.nearest} label="nearest" color="orange" />}
                        </div>
                      )}
                    </div>
                    <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground transition-all duration-300 flex-shrink-0 ml-2 ${openSections.environment ? 'rotate-180' : ''}`} />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-2 pb-4 sm:pb-6 px-4 sm:px-6 bg-green-500/[0.02]">
                    {parsed.intro && (
                      <p className="text-base font-medium text-foreground leading-relaxed">
                        {highlightStats(parsed.intro)}
                      </p>
                    )}
                    
                    {parsed.bullets.length > 0 && (
                      <ul className="space-y-2.5">
                        {parsed.bullets.map((bullet, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                            <span className="text-green-700 font-bold mt-0.5">•</span>
                            <span className="flex-1">{highlightStats(bullet)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {parsed.closing && (
                      <p className="text-sm text-foreground font-medium italic mt-3">
                        {highlightStats(parsed.closing)}
                      </p>
                    )}
                    
                    <div className="mt-4">
                      {getImpactBadge(impact)}
                    </div>
                    
                    {!report && quickFacts && (
                      <p className="text-xs text-green-600/70 italic mt-2">
                        🤖 AI-enhanced insights loading...
                      </p>
                    )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          );
        })()}

        {/* Demographics - Show from quick facts OR full report */}
        {(report?.demographics || quickFacts?.scores?.demographics) && (() => {
          const summary = report?.demographics?.summary || quickFacts?.scores?.demographics?.summary || '';
          const impact = report?.demographics?.impact || quickFacts?.scores?.demographics?.impact || '';
          const parsed = parseSummary(summary);
          
          return (
          <Collapsible open={openSections.demographics} onOpenChange={() => toggleSection("demographics")}>
            <Card className="group shadow-md hover:shadow-xl transition-all duration-300 border-2 border-purple-500/20 hover:border-purple-500/40 bg-gradient-to-r from-purple-500/5 to-transparent">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between hover:bg-purple-500/5 transition-colors rounded-t-lg p-4 sm:p-6">
                    <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-purple-500/20 to-purple-600/10 group-hover:from-purple-500/30 group-hover:to-purple-600/20 transition-colors shadow-sm flex-shrink-0">
                      <Users className="h-5 w-5 sm:h-6 sm:w-6 text-purple-700" />
                    </div>
                        <div className="text-left flex-1">
                      <span className="text-base sm:text-lg md:text-xl font-bold text-foreground">Demographics</span>
                      <p className="text-xs text-purple-700/80 font-medium">Community & People</p>
                    </div>
                  </CardTitle>
                      
                      {!openSections.demographics && parsed.intro && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{parsed.intro.substring(0, 80)}...</p>
                      )}
                    </div>
                    <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground transition-all duration-300 flex-shrink-0 ml-2 ${openSections.demographics ? 'rotate-180' : ''}`} />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-2 pb-4 sm:pb-6 px-4 sm:px-6 bg-purple-500/[0.02]">
                    {parsed.intro && (
                      <p className="text-base font-medium text-foreground leading-relaxed">
                        {highlightStats(parsed.intro)}
                      </p>
                    )}
                    
                    {parsed.bullets.length > 0 && (
                      <ul className="space-y-2.5">
                        {parsed.bullets.map((bullet, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                            <span className="text-purple-700 font-bold mt-0.5">•</span>
                            <span className="flex-1">{highlightStats(bullet)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {parsed.closing && (
                      <p className="text-sm text-foreground font-medium italic mt-3">
                        {highlightStats(parsed.closing)}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-3 mt-4">
                      <span className="text-3xl">{getDemographicEmoji(impact)}</span>
                      <span className="text-sm text-muted-foreground capitalize">{impact}</span>
                  </div>
                    
                    {!report && quickFacts && (
                      <p className="text-xs text-purple-600/70 italic mt-2">
                        🤖 AI-enhanced insights loading...
                      </p>
                    )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          );
        })()}

        {/* Market Dynamics - Show from quick facts OR full report */}
        {(report?.market_dynamics || quickFacts?.scores?.market_dynamics) && (() => {
          const summary = report?.market_dynamics?.summary || quickFacts?.scores?.market_dynamics?.summary || '';
          const impact = report?.market_dynamics?.impact || quickFacts?.scores?.market_dynamics?.impact || '';
          const parsed = parseSummary(summary);
          const headlines = getMarketHeadline(summary);
          
          return (
          <Collapsible open={openSections.market} onOpenChange={() => toggleSection("market")}>
            <Card className="group shadow-md hover:shadow-xl transition-all duration-300 border-2 border-cyan-500/20 hover:border-cyan-500/40 bg-gradient-to-r from-cyan-500/5 to-transparent">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between hover:bg-cyan-500/5 transition-colors rounded-t-lg p-4 sm:p-6">
                    <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 group-hover:from-cyan-500/30 group-hover:to-cyan-600/20 transition-colors shadow-sm flex-shrink-0">
                      <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-cyan-700" />
                    </div>
                        <div className="text-left flex-1">
                      <span className="text-base sm:text-lg md:text-xl font-bold text-foreground">Market Dynamics</span>
                      <p className="text-xs text-cyan-700/80 font-medium">Trends & Value</p>
                    </div>
                  </CardTitle>
                      
                      {!openSections.market && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {headlines.price && <StatBadge value={headlines.price} color="green" />}
                          {headlines.growth && <StatBadge value={headlines.growth} label="growth" color="blue" />}
                          {headlines.days && <StatBadge value={headlines.days} label="on market" color="orange" />}
                        </div>
                      )}
                    </div>
                    <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground transition-all duration-300 flex-shrink-0 ml-2 ${openSections.market ? 'rotate-180' : ''}`} />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-2 pb-4 sm:pb-6 px-4 sm:px-6 bg-cyan-500/[0.02]">
                    {parsed.intro && (
                      <p className="text-base font-medium text-foreground leading-relaxed">
                        {highlightStats(parsed.intro)}
                      </p>
                    )}
                    
                    {parsed.bullets.length > 0 && (
                      <ul className="space-y-2.5">
                        {parsed.bullets.map((bullet, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                            <span className="text-cyan-700 font-bold mt-0.5">•</span>
                            <span className="flex-1">{highlightStats(bullet)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {parsed.closing && (
                      <p className="text-sm text-foreground font-medium italic mt-3">
                        {highlightStats(parsed.closing)}
                      </p>
                    )}
                    
                    <div className="mt-4">
                      {getMarketTrend(impact)}
                    </div>
                    
                    {!report && quickFacts && (
                      <p className="text-xs text-cyan-600/70 italic mt-2">
                        🤖 AI-enhanced insights loading...
                      </p>
                    )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          );
        })()}

        {/* Gentrification Index - Show from quick facts OR full report */}
        {(report?.gentrification_index || quickFacts?.scores?.gentrification_index) && (() => {
          const summary = report?.gentrification_index?.summary || quickFacts?.scores?.gentrification_index?.summary || '';
          const impact = report?.gentrification_index?.impact || quickFacts?.scores?.gentrification_index?.impact || '';
          const parsed = parseSummary(summary);
          
          return (
          <Collapsible open={openSections.gentrification} onOpenChange={() => toggleSection("gentrification")}>
            <Card className="group shadow-md hover:shadow-xl transition-all duration-300 border-2 border-pink-500/20 hover:border-pink-500/40 bg-gradient-to-r from-pink-500/5 to-transparent">
              <CollapsibleTrigger className="w-full">
                <CardHeader className="flex flex-row items-center justify-between hover:bg-pink-500/5 transition-colors rounded-t-lg p-4 sm:p-6">
                    <div className="flex-1">
                  <CardTitle className="flex items-center gap-2 sm:gap-4">
                    <div className="p-2 sm:p-3 rounded-xl bg-gradient-to-br from-pink-500/20 to-pink-600/10 group-hover:from-pink-500/30 group-hover:to-pink-600/20 transition-colors shadow-sm flex-shrink-0">
                      <TrendingUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-pink-700" />
                    </div>
                        <div className="text-left flex-1">
                      <span className="text-base sm:text-lg md:text-xl font-bold text-foreground">Gentrification Index</span>
                      <p className="text-xs text-pink-700/80 font-medium">Hipster Factor</p>
                    </div>
                  </CardTitle>
                      
                      {!openSections.gentrification && parsed.intro && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{parsed.intro.substring(0, 80)}...</p>
                      )}
                    </div>
                    <ChevronDown className={`h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground transition-all duration-300 flex-shrink-0 ml-2 ${openSections.gentrification ? 'rotate-180' : ''}`} />
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-2 pb-4 sm:pb-6 px-4 sm:px-6 bg-pink-500/[0.02]">
                    {parsed.intro && (
                      <p className="text-base font-medium text-foreground leading-relaxed">
                        {highlightStats(parsed.intro)}
                      </p>
                    )}
                    
                    {parsed.bullets.length > 0 && (
                      <ul className="space-y-2.5">
                        {parsed.bullets.map((bullet, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-muted-foreground">
                            <span className="text-pink-700 font-bold mt-0.5">•</span>
                            <span className="flex-1">{highlightStats(bullet)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    
                    {parsed.closing && (
                      <p className="text-sm text-foreground font-medium italic mt-3">
                        {highlightStats(parsed.closing)}
                      </p>
                    )}
                    
                    <p className="text-sm font-medium text-pink-700 mt-4">
                      {impact}
                    </p>
                    
                    {!report && quickFacts && (
                      <p className="text-xs text-pink-600/70 italic mt-2">
                        🤖 AI-enhanced insights loading...
                      </p>
                    )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          );
        })()}
      </div>

      {/* Perspective Sections Divider */}
      {(report?.investor_thought || report?.owner_occupier_thought || (isLoadingAI && !report)) && (
        <>
          <div className="flex items-center gap-2 sm:gap-4 py-6 sm:py-8">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-emerald-500/50 to-cyan-500/50"></div>
            <div className="flex flex-col items-center gap-0.5 sm:gap-1 px-2">
              <span className="text-xs sm:text-sm font-bold bg-gradient-to-r from-emerald-600 to-cyan-600 bg-clip-text text-transparent uppercase tracking-wider text-center">
                💭 Expert Perspectives
              </span>
              <span className="text-xs text-muted-foreground hidden sm:block">The realtor won't tell you this 🤫</span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/50 via-emerald-500/50 to-transparent"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {/* Investor Thought Section - Show skeleton while loading */}
            {isLoadingAI && !report?.investor_thought && (
              <Card className="relative overflow-hidden shadow-xl border-2 border-gray-300/30 bg-gradient-to-br from-gray-300/10 via-gray-300/5 to-transparent animate-pulse">
                <CardHeader className="relative pb-3 sm:pb-4 pt-4 sm:pt-6">
                  <CardTitle className="flex items-center gap-3 sm:gap-4">
                    <div className="h-14 w-14 bg-gray-300 rounded-xl flex-shrink-0"></div>
                    <div className="space-y-2 flex-1">
                      <div className="h-6 bg-gray-300 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative pt-2 pb-4 sm:pb-6 px-4 sm:px-6">
                  <div className="bg-background/80 backdrop-blur-sm rounded-lg p-4 sm:p-5 border border-gray-300/20 space-y-3">
                    <div className="h-4 bg-gray-300 rounded w-full"></div>
                    <div className="h-4 bg-gray-300 rounded w-full"></div>
                    <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-300 rounded w-4/5"></div>
                  </div>
                </CardContent>
              </Card>
            )}
            {report?.investor_thought && (
              <Card className="relative overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-emerald-500/30 hover:border-emerald-500/50 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent group">
                <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-emerald-500/10 rounded-full blur-3xl group-hover:bg-emerald-500/20 transition-all"></div>
                <CardHeader className="relative pb-3 sm:pb-4 pt-4 sm:pt-6">
                  <CardTitle className="flex items-center gap-3 sm:gap-4">
                    <div className="p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 shadow-lg group-hover:from-emerald-500/40 group-hover:to-emerald-600/30 transition-all flex-shrink-0">
                      <Wallet className="h-6 w-6 sm:h-7 sm:w-7 text-emerald-700" />
                    </div>
                    <div className="text-left">
                      <span className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-emerald-700 to-emerald-600 bg-clip-text text-transparent">
                        Investor Thought
                      </span>
                      <p className="text-xs text-emerald-600 font-semibold mt-0.5 sm:mt-1">
                        💰 The Investment Lens
                      </p>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative pt-2 pb-4 sm:pb-6 px-4 sm:px-6">
                  <div className="bg-background/80 backdrop-blur-sm rounded-lg p-4 sm:p-5 border border-emerald-500/20">
                    <p className="text-foreground leading-relaxed text-sm sm:text-base font-medium">
                      {report.investor_thought}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Owner Occupier Thought Section - Show skeleton while loading */}
            {isLoadingAI && !report?.owner_occupier_thought && (
              <Card className="relative overflow-hidden shadow-xl border-2 border-gray-300/30 bg-gradient-to-br from-gray-300/10 via-gray-300/5 to-transparent animate-pulse">
                <CardHeader className="relative pb-3 sm:pb-4 pt-4 sm:pt-6">
                  <CardTitle className="flex items-center gap-3 sm:gap-4">
                    <div className="h-14 w-14 bg-gray-300 rounded-xl flex-shrink-0"></div>
                    <div className="space-y-2 flex-1">
                      <div className="h-6 bg-gray-300 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative pt-2 pb-4 sm:pb-6 px-4 sm:px-6">
                  <div className="bg-background/80 backdrop-blur-sm rounded-lg p-4 sm:p-5 border border-gray-300/20 space-y-3">
                    <div className="h-4 bg-gray-300 rounded w-full"></div>
                    <div className="h-4 bg-gray-300 rounded w-full"></div>
                    <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-300 rounded w-4/5"></div>
                  </div>
                </CardContent>
              </Card>
            )}
            {report?.owner_occupier_thought && (
              <Card className="relative overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-cyan-500/30 hover:border-cyan-500/50 bg-gradient-to-br from-cyan-500/10 via-cyan-500/5 to-transparent group">
                <div className="absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-cyan-500/10 rounded-full blur-3xl group-hover:bg-cyan-500/20 transition-all"></div>
                <CardHeader className="relative pb-3 sm:pb-4 pt-4 sm:pt-6">
                  <CardTitle className="flex items-center gap-3 sm:gap-4">
                    <div className="p-3 sm:p-4 rounded-xl sm:rounded-2xl bg-gradient-to-br from-cyan-500/30 to-cyan-600/20 shadow-lg group-hover:from-cyan-500/40 group-hover:to-cyan-600/30 transition-all flex-shrink-0">
                      <Home className="h-6 w-6 sm:h-7 sm:w-7 text-cyan-700" />
                    </div>
                    <div className="text-left">
                      <span className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-cyan-700 to-cyan-600 bg-clip-text text-transparent">
                        Owner Occupier
                      </span>
                      <p className="text-xs text-cyan-600 font-semibold mt-0.5 sm:mt-1">
                        🏠 The Liveability Lens
                      </p>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="relative pt-2 pb-4 sm:pb-6 px-4 sm:px-6">
                  <div className="bg-background/80 backdrop-blur-sm rounded-lg p-4 sm:p-5 border border-cyan-500/20">
                    <p className="text-foreground leading-relaxed text-sm sm:text-base font-medium">
                      {report.owner_occupier_thought}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Footer Action */}
      <div className="pt-4 space-y-4 sm:space-y-6">
        <Button
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            onReset();
          }}
          size="lg"
          variant="outline"
          className="w-full h-14 sm:h-16 text-base sm:text-lg md:text-xl rounded-xl sm:rounded-2xl shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all font-bold bg-gradient-to-r from-primary/10 to-primary/5 hover:from-primary/20 hover:to-primary/10 border-2 border-primary/30 hover:border-primary/50"
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <MapPin className="h-5 w-5 sm:h-6 sm:w-6" />
            <span>Search Another Property</span>
          </div>
        </Button>
      </div>

      {/* Mobile Floating Action Button */}
      <div className="fixed bottom-6 right-6 sm:hidden z-40 no-print">
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleShare}
            size="icon"
            className="h-12 w-12 rounded-full shadow-lg bg-primary hover:bg-primary/90"
          >
            {copied ? <Check className="h-5 w-5" /> : <Share2 className="h-5 w-5" />}
          </Button>
          <Button
            onClick={handlePrint}
            size="icon"
            variant="outline"
            className="h-12 w-12 rounded-full shadow-lg bg-background"
          >
            <Printer className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
